import { Fab } from '@/components/elements/forms/buttons';
import { TIME_FILTER } from '@/constants/timeConstants';
import { RatingSkeleton } from '@/components/elements/redesign/Rating/Rating';
import { RatingV2 } from '@/components/elements/redesign/RatingV2/RatingV2';
import Icon from '@/components/icons/Icon';
import { shouldDisplayReviews } from '@/helpers';
import { useAppSelector, useAppDispatch } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED } from '@/constants/reviewConstants';
import { modalActions } from '@/actions/modalActions';
import { FormState } from '@/reducers/formReducer';
import { REVIEWS_V2_LISTCARDS_V2_FLAG } from '@/constants/experimentConstants';
import { useConfidenceFlag } from '@/hooks/useConfidenceFlag';

const baseTranslationKey = 'features.PlacePages.modules.Header';

const REVIEWS_FALLBACK = { stats: { count: 0, score: 0 } };

const PlacePageHeader = ({
  placeId,
  reviewsUid,
  onFavoriteClick,
}: {
  placeId: number;
  onReviewsClick?: () => void;
  reviewsUid?: string;
  onFavoriteClick?: () => void;
}) => {
  const { isMobileView } = useMobileView();
  const place = useAppSelector((state) => state.place[placeId]);
  const reviewsDetails = useAppSelector((state) => state.reviews.byUid?.[reviewsUid]?.details) || REVIEWS_FALLBACK;
  const reviewsSearchResult = useAppSelector((state) => state.reviews.byUid?.[reviewsUid]?.searchResult);

  const { flagValue: reviewsV2FlagValue } = useConfidenceFlag(
    REVIEWS_V2_LISTCARDS_V2_FLAG.name,
    REVIEWS_V2_LISTCARDS_V2_FLAG.defaultValue,
    true,
  );
  const isReviewsV2Enabled = Boolean(reviewsV2FlagValue?.['reviews-v2']);

  const timeFilterFromState = useAppSelector(
    (state: { form: FormState }) => state.form[reviewsUid]?.searchFilter?.timeFilter,
  );

  const { about = {}, contact = {} } = place;

  const address = contact.address ? contact.address : null;
  const { stats = {} } = reviewsDetails;
  const { count, score } = stats;
  const user = useAppSelector((state) => state.users.user);
  const isFavorite = user ? user.favorites.indexOf(place.id) !== -1 : false;
  const reduxDispatch = useAppDispatch();

  const onReviewsClick = () => {
    if (isReviewsV2Enabled) {
      reduxDispatch(
        modalActions.reviews({
          focus: 'rating',
          placeId: placeId,
          context: 'placePage',
          timeFilter: timeFilterFromState || TIME_FILTER.FOREVER,
          employeeId: undefined,
          serviceId: undefined,
          show: true,
        }),
      );
      return;
    }

    document.getElementById('reviews').scrollIntoView();
    window.location.replace('#reviews'); //Go to the target element.
  };

  return (
    <div className={`gap-xl flex ${isMobileView ? 'flex-col' : 'justify-between'}`}>
      <div className={`flex flex-col ${isMobileView ? 'gap-xs' : 'gap-md'}`}>
        <div className="flex items-center justify-between">
          <h1 className={`text-fg_primary ${isMobileView ? 'text-h-l' : 'text-h-xl'} font-bold`}>{about.name}</h1>
          {isMobileView && (
            <Fab
              size="md"
              className="shrink-0"
              onClick={onFavoriteClick}
              aria-label={_t(`${baseTranslationKey}.favorites.${isFavorite ? 'remove' : 'add'}`)}
              variant="secondary"
              icon={<Icon variant={`heart${isFavorite ? '-filled' : ''}`} color="fg_primary" />}></Fab>
          )}
        </div>

        {(address?.zipcode || address?.city) && (
          <div className="text-fg_tecondary text-m pr-2xs flex">
            {address?.street && <span>{address.street.trim() + ','}&nbsp;</span>}
            {address?.zipcode && <span>{address.zipcode.trim()}</span>}
            {address?.city && <span>&nbsp;{address.city.trim()}</span>}
          </div>
        )}
      </div>
      {shouldDisplayReviews(place) && (
        <div className={`flex flex-[1_1_0%] ${isMobileView ? 'items-center' : 'items-start justify-end'}`}>
          {reviewsSearchResult?.fetched && !reviewsSearchResult?.loading ? (
            <RatingV2
              ratingCount={count >= MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED ? count : 0}
              averageRating={score}
              size={isMobileView ? 'md' : 'lg'}
              direction={isMobileView ? 'row' : 'column'}
              withAverageScore={count >= MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED}
              withRatingCounter
              onReviewsClick={count >= MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED ? onReviewsClick : undefined}
            />
          ) : (
            <RatingSkeleton />
          )}
        </div>
      )}
    </div>
  );
};

export default PlacePageHeader;
